import { normalizeClientOrderData } from '@shared/engagement-order-data';

import {
  type EngagementFilters,
  type EngagementItem,
  type EngagementStats,
  NEEDS_YOU_SENTIMENTS,
} from '../../types/engagement';
import { EnhancedBaseService } from '../base/EnhancedBaseService';
import { ServiceError } from '../base/ServiceError';
import type { QueryOptions, ServiceResponse } from '../base/ServiceTypes';

export class EngagementService extends EnhancedBaseService {
  private static instance: EngagementService;

  public static getInstance(): EngagementService {
    if (!EngagementService.instance) {
      EngagementService.instance = new EngagementService({
        deduplication: { enabled: true, ttl: 10000 },
        smartCaching: { enabled: true },
      });
    }
    return EngagementService.instance;
  }

  async fetchItems(
    filters?: EngagementFilters,
    options?: QueryOptions,
  ): Promise<ServiceResponse<EngagementItem[]>> {
    const userId = await this.ensureAuthenticated();
    const dedupKey = `fetch-engagement-items-${userId}-${JSON.stringify(filters ?? {})}`;

    return this.executeWithDedup(async () => {
      const cacheKey = `engagement-items-${userId}-${JSON.stringify(filters ?? {})}`;

      const cached = this.getFromCache<EngagementItem[]>(cacheKey);
      if (cached && !options?.skipCache) {
        return cached;
      }

      let query = this.supabase
        .from('engagement_items')
        .select('*')
        // DB-side triage order (Slice 1c): most urgent sentiment first, then most
        // recent within a priority band. Backed by idx_engagement_items_user_priority_created.
        .order('sentiment_priority', { ascending: true })
        .order('platform_created_at', { ascending: false });

      // 2026-08-28 cross-workspace contamination fix: engagement_items pools
      // account-wide with no RLS workspace boundary (a separate, already-
      // staged migration owns that axis — see CLAUDE.md), so the app layer is
      // the only scoping seam. Same `.or(brand_kit_id.eq,is.null)` shape as
      // PostService/CampaignService/etc. — a workspace's own attributed rows
      // plus true legacy-NULL rows, never a sibling workspace's own rows.
      if (filters?.activeBrandKitId) {
        query = query.or(`brand_kit_id.eq.${filters.activeBrandKitId},brand_kit_id.is.null`);
      }

      if (!filters?.includeHidden) {
        query = query.neq('status', 'hidden');
      }

      if (filters?.platform) {
        query = query.eq('platform', filters.platform);
      }
      if (filters?.type) {
        query = query.eq('item_type', filters.type);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      } else if (!filters?.includeReplied) {
        // Triage inbox default: already-replied items stayed mixed in with
        // unread ones, creating a false backlog (UAT 2026-06-13, F-091).
        // `includeReplied` (the "Done" tab) opts back in and filters client-side.
        query = query.neq('status', 'replied');
      }

      const { data, error } = await query;

      if (error) {
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          return [];
        }
        throw this.createError(error.message, 'QUERY_ERROR', error);
      }

      // The DB stores lookupOrderInfo's nested `{found, order:{...}}`; the UI reads flat
      // fields (name, line_items, ...). Normalise at this boundary.
      const items = ((data ?? []) as unknown as EngagementItem[]).map((row) =>
        row.shopify_order_data == null
          ? row
          : { ...row, shopify_order_data: normalizeClientOrderData(row.shopify_order_data) },
      );
      this.setCache(cacheKey, items);
      return items;
    }, dedupKey);
  }

  async getStats(
    activeBrandKitId?: string | null,
    options?: QueryOptions,
  ): Promise<ServiceResponse<EngagementStats>> {
    const userId = await this.ensureAuthenticated();
    const dedupKey = `get-engagement-stats-${userId}-${activeBrandKitId ?? 'none'}`;

    return this.executeWithDedup(async () => {
      const cacheKey = `engagement-stats-${userId}-${activeBrandKitId ?? 'none'}`;

      const cached = this.getFromCache<EngagementStats>(cacheKey);
      if (cached && !options?.skipCache) {
        return cached;
      }

      let statsQuery = this.supabase
        .from('engagement_items')
        .select('item_type, status, sentiment');
      // Same workspace boundary as fetchItems — see its comment above.
      if (activeBrandKitId) {
        statsQuery = statsQuery.or(`brand_kit_id.eq.${activeBrandKitId},brand_kit_id.is.null`);
      }
      const { data, error } = await statsQuery;

      // Fetch hidden rows only to calculate the recoverable-tab count;
      // visibleRows below keeps them out of every active-work count.
      if (error) {
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          return {
            total: 0,
            unread: 0,
            comments: 0,
            dms: 0,
            reviews: 0,
            flagged: 0,
            needsYou: 0,
            done: 0,
            hidden: 0,
          };
        }
        throw this.createError(error.message, 'QUERY_ERROR', error);
      }

      const rows = data ?? [];
      const visibleRows = rows.filter((r) => r.status !== 'hidden');
      const needsYouSet = new Set<string>(NEEDS_YOU_SENTIMENTS);
      const stats: EngagementStats = {
        total: visibleRows.length,
        unread: visibleRows.filter((r) => r.status === 'unread').length,
        comments: visibleRows.filter((r) => r.item_type === 'comment').length,
        dms: visibleRows.filter((r) => r.item_type === 'dm').length,
        reviews: visibleRows.filter((r) => r.item_type === 'review').length,
        flagged: visibleRows.filter((r) => r.status === 'flagged').length,
        // Actionable backlog: needs-you sentiment that isn't already handled.
        needsYou: visibleRows.filter(
          (r) =>
            r.sentiment != null &&
            needsYouSet.has(r.sentiment) &&
            r.status !== 'replied' &&
            r.status !== 'hidden',
        ).length,
        done: visibleRows.filter((r) => r.status === 'replied').length,
        hidden: rows.filter((r) => r.status === 'hidden').length,
      };

      this.setCache(cacheKey, stats);
      return stats;
    }, dedupKey);
  }

  /**
   * Bulk "seen the inbox" transition: flip only the still-`unread` rows among the
   * given ids to `read`. Scoped to `status='unread'` so it never clobbers a
   * `replied`/`flagged`/`hidden`/`sending` item the user already triaged, and to
   * the supplied ids so it respects whatever filter the hub is currently showing.
   */
  async markRead(ids: string[]): Promise<ServiceResponse<number>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      if (ids.length === 0) return 0;

      const { data, error } = await this.supabase
        .from('engagement_items')
        .update({ status: 'read', updated_at: new Date().toISOString() })
        .eq('status', 'unread')
        .in('id', ids)
        .select('id');

      if (error) {
        throw this.createError(error.message, 'UPDATE_ERROR', error);
      }

      this.invalidateCacheByPattern(`engagement-items-${userId}`);
      this.invalidateCacheByPattern(`engagement-stats-${userId}`);

      return data?.length ?? 0;
    });
  }

  /**
   * "Mark all read" — flip EVERY still-`unread` row for the user to `read` in one
   * query, regardless of the hub's current tab/platform filter. This is the
   * distinct value vs the per-view mark-on-view effect: it clears the whole unread
   * badge (inbox-zero on unread), including items in tabs the user hasn't opened.
   */
  async markAllRead(): Promise<ServiceResponse<number>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      const { data, error } = await this.supabase
        .from('engagement_items')
        .update({ status: 'read', updated_at: new Date().toISOString() })
        .eq('status', 'unread')
        .select('id');

      if (error) {
        throw this.createError(error.message, 'UPDATE_ERROR', error);
      }

      this.invalidateCacheByPattern(`engagement-items-${userId}`);
      this.invalidateCacheByPattern(`engagement-stats-${userId}`);

      return data?.length ?? 0;
    });
  }

  async hideItem(id: string): Promise<ServiceResponse<EngagementItem>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      const { data, error } = await this.supabase
        .from('engagement_items')
        .update({ status: 'hidden', updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw this.createError(error.message, 'UPDATE_ERROR', error);
      }

      this.invalidateCacheByPattern(`engagement-items-${userId}`);
      this.invalidateCacheByPattern(`engagement-stats-${userId}`);

      return data as EngagementItem;
    });
  }

  /** Restore a hidden item to the open inbox without treating it as unread. */
  async unhideItem(id: string): Promise<ServiceResponse<EngagementItem>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      const { data, error } = await this.supabase
        .from('engagement_items')
        .update({ status: 'read', updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('status', 'hidden')
        .select()
        .single();

      if (error) {
        throw this.createError(error.message, 'UPDATE_ERROR', error);
      }

      this.invalidateCacheByPattern(`engagement-items-${userId}`);
      this.invalidateCacheByPattern(`engagement-stats-${userId}`);

      return data as EngagementItem;
    });
  }

  /**
   * Bulk hide (e.g. "Clear spam"). Scoped to the supplied ids + user, and guarded
   * to NOT touch a row mid-send (`status='sending'`) so a reply about to post isn't
   * yanked out from under it, nor re-hide an already-hidden row (idempotent).
   * Returns the ids actually hidden (`.select('id')`) so the caller can reconcile
   * partial success instead of all-or-nothing.
   */
  async markHidden(ids: string[]): Promise<ServiceResponse<string[]>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      if (ids.length === 0) return [];

      const { data, error } = await this.supabase
        .from('engagement_items')
        .update({ status: 'hidden', updated_at: new Date().toISOString() })
        .in('id', ids)
        .not('status', 'in', '("hidden","sending")')
        .select('id');

      if (error) {
        throw this.createError(error.message, 'UPDATE_ERROR', error);
      }

      this.invalidateCacheByPattern(`engagement-items-${userId}`);
      this.invalidateCacheByPattern(`engagement-stats-${userId}`);

      return (data ?? []).map((r) => r.id as string);
    });
  }

  async flagItem(id: string): Promise<ServiceResponse<EngagementItem>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      const { data, error } = await this.supabase
        .from('engagement_items')
        .update({ status: 'flagged', updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw this.createError(error.message, 'UPDATE_ERROR', error);
      }

      this.invalidateCacheByPattern(`engagement-items-${userId}`);
      this.invalidateCacheByPattern(`engagement-stats-${userId}`);

      return data as EngagementItem;
    });
  }

  /** Remove a follow-up flag and return the item to the read inbox. */
  async unflagItem(id: string): Promise<ServiceResponse<EngagementItem>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      const { data, error } = await this.supabase
        .from('engagement_items')
        .update({ status: 'read', updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('status', 'flagged')
        .select()
        .single();

      if (error) {
        throw this.createError(error.message, 'UPDATE_ERROR', error);
      }

      this.invalidateCacheByPattern(`engagement-items-${userId}`);
      this.invalidateCacheByPattern(`engagement-stats-${userId}`);

      return data as EngagementItem;
    });
  }

  async updateSuggestedReply(
    id: string,
    reply: string,
  ): Promise<ServiceResponse<EngagementItem | null>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      // maybeSingle() returns null (no error) when the row doesn't exist,
      // instead of throwing a 406. Tolerates stale ids from re-seeds or
      // concurrent mutations — the edge function also writes the suggestion
      // so a missed client-side update doesn't lose data.
      const { data, error } = await this.supabase
        .from('engagement_items')
        .update({ suggested_reply: reply, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .maybeSingle();

      if (error) {
        throw this.createError(error.message, 'UPDATE_ERROR', error);
      }

      this.invalidateCacheByPattern(`engagement-items-${userId}`);

      return (data as EngagementItem | null) ?? null;
    });
  }

  async suggestReply(params: {
    engagement_item_id: string;
    message_text: string;
    platform: string;
    item_type: string;
    post_context?: string;
    order_data?: any;
    sentiment?: string;
    // Slice 2 multi-turn refine: pass the current draft + instruction to revise
    // it in place (stateless — the server holds no session).
    prior_suggestion?: string;
    refine_instruction?: string;
  }): Promise<
    ServiceResponse<{ suggested_reply: string; confidence: number; grounding?: string }>
  > {
    return this.execute(async () => {
      const { data } = await this.client.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw this.createError('Unauthorized', 'UNAUTHORIZED', null);

      const res = await fetch('/api/engagement/suggest-reply', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(params),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => null);
        throw this.createError(
          errorData?.error ?? `API error ${res.status}`,
          'API_ERROR',
          errorData,
        );
      }

      return res.json();
    });
  }

  async sendReply(params: {
    engagement_item_id: string;
    reply_text: string;
    platform: string;
    item_type: string;
    external_id: string;
    // GBP contract change (Slice 3d): a Google Business Profile user has MANY location
    // rows, so the edge fn can't pick one with `.eq('platform',platform).limit(1)`.
    // For reviews the location is derived server-side from external_id
    // (accounts/*/locations/*/reviews/*); this optional field lets a caller pin the
    // location (locations/{id}) explicitly for future multi-account GBP item types.
    platform_user_id?: string;
  }): Promise<ServiceResponse<{ success: boolean }>> {
    return this.execute(async () => {
      const { data, error } = await this.supabase.functions.invoke('send-engagement-reply', {
        body: params,
      });
      if (error) {
        const message = await ServiceError.extractEdgeFunctionMessage(error);
        throw this.createError(message, 'FUNCTION_ERROR', error);
      }
      return data;
    });
  }

  clearAllCache(): void {
    this.invalidateCacheByPattern('engagement-items-');
    this.invalidateCacheByPattern('engagement-stats-');
  }
}

export const engagementService = EngagementService.getInstance();
